import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import ButtonBase from '@material-ui/core/ButtonBase';

import Menu from 'components/common/Menu';
import IconTriangleBottom from 'components/icons/IconTriangleBottom';
import AutosuggestField from 'components/fields/AutosuggestField';
import Tooltip from 'components/common/Tooltip';
import Typography from '@material-ui/core/Typography';

import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';
import hexToRgb from 'styles/hexToRgb';

const styles = theme => ({
  root: {
    position: 'relative',
    maxWidth: '100%',
    margin: 0,
  },
  label: {
    position: 'relative',
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
    fontSize: pxToRem(12),
    letterSpacing: pxToEm(0.5),
    fontWeight: '700',
    color: theme.palette.grey[600],
    transform: 'none',
  },
  selectRoot: {
    display: 'block',
    padding: 0,
    width: '100%',
    position: 'relative',
    fontFamily: theme.typography.fontFamily,
  },
  selectSelect: {
    width: 'auto',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    lineHeight: 'calc(1em + 14px)',
    height: 'calc(1em + 14px)',
    cursor: 'pointer',
    textOverflow: 'ellipsis',
    textAlign: 'left',
    border: `1px solid ${theme.palette.grey[600]}`,
    userSelect: 'none',
    borderRadius: 4,
    color: theme.palette.secondary[800],
    padding: '0 34px 0 15px',
    '&:focus': {
      borderRadius: 4,
      borderColor: theme.palette.primary[500],
      backgroundColor: `rgba(${hexToRgb(theme.palette.primary[500])}, 0.1)`,
    },
  },
  selectSelectSm: {
    fontSize: pxToRem(13),
    letterSpacing: pxToEm(0.25),
  },
  selectSelectMd: {
    fontSize: pxToRem(14),
    letterSpacing: pxToEm(0.5),
  },
  selectSelectLg: {
    fontSize: pxToRem(14),
    letterSpacing: pxToEm(0.5),
    paddingTop: 3,
    paddingBottom: 4,
  },
  selectSelectXl: {
    fontSize: pxToRem(16),
    letterSpacing: pxToEm(0.5, 16),
    paddingTop: 3,
    paddingBottom: 3,
  },
  selectSelectDark: {
    backgroundColor: theme.palette.grey[100],
  },
  selectSelectLight: {
    backgroundColor: theme.palette.common.white,
  },
  selectSelectActive: {
    borderColor: theme.palette.primary[500],
    backgroundColor: `rgba(${hexToRgb(theme.palette.primary[500])}, 0.1)`,
  },
  selectSelectDisabled: {
    color: `rgba(${hexToRgb(theme.palette.secondary[800])}, 0.4)`,
  },
  selectSelectError: {
    borderColor: theme.palette.validation[500],
  },
  selectAdorment: {
    position: 'absolute',
    top: '50%',
    right: 13,
    pointerEvents: 'none',
    transform: 'translateY(-50%)',
  },
  iconTriangleBottom: {
    display: 'block',
    width: 10,
    height: 5,
    color: theme.palette.grey[600],
  },
  suggestionsContainerOpen: {
    position: 'relative',
    boxShadow: 'none',
  },
  textField: {
    boxSizing: 'border-box',
    padding: '10px 20px',
  },
});

class SelectAutosuggestField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      hovered: false,
      autosuggestValue: '',
    };

    this.onAutosuggestChange = this.onAutosuggestChange.bind(this);
    this.onSelectRootClick = this.onSelectRootClick.bind(this);
    this.onSelectRootKeyDown = this.onSelectRootKeyDown.bind(this);
    this.onSelectRootBlur = this.onSelectRootBlur.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onButtonMouseOver = this.onButtonMouseOver.bind(this);
    this.onButtonMouseOut = this.onButtonMouseOut.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if ((nextProps.input.value !== this.props.input.value)
      || (nextProps.disabled && !this.props.disabled)) {
      this.setState({ anchorEl: null });
    }

    if (!this.props.disabled && this.props.meta.active && nextProps.disabled) {
      this.props.input.onBlur();
    }
  }

  onAutosuggestChange(event) {
    if (event.target) {
      this.setState({ autosuggestValue: event.target.value });
    } else {
      this.setState({ autosuggestValue: event });
    }
  }

  onSelectRootClick(event) {
    this.setState({ anchorEl: event.currentTarget });
  }

  onSelectRootKeyDown(event) {
    if (event.key === 'ArrowDown') {
      this.setState({ anchorEl: event.currentTarget });
    }
  }

  onSelectRootBlur() {
    if (!this.state.anchorEl) {
      this.props.input.onBlur();
    }
  }

  onMenuClose() {
    this.setState({
      anchorEl: null,
      autosuggestValue: '',
    });
  }

  onButtonMouseOver() {
    this.setState({ hovered: true });
  }

  onButtonMouseOut() {
    this.setState({ hovered: false });
  }

  render() {
    const {
      input,
      meta,
      placeholder,
      renderSuggestionContent,
      renderInputContent,
      fetchSuggestions,
      onSuggestionSelected,
      onListAdornmentClick,
      listAdornmentText,
      id,
      limit,
      label,
      className,
      classes,
      size,
      color,
      disabled,
      fullWidth,
      placement,
      keepOpenAfterSelection,
    } = this.props;
    const open = Boolean(this.state.anchorEl);
    const { hovered } = this.state;
    const errorVisible =
      Boolean(meta.touched)
      && Boolean(meta.error)
      && Boolean(!meta.error.codes)
      && hovered;
    return (
      <FormControl
        className={classNames(classes.root, className)}
        fullWidth={fullWidth}
      >
        {label.length > 0 &&
          <InputLabel
            className={classNames(classes.label)}
            disableAnimation
            htmlFor={input.id}
            shrink={false}
          >
            {label}
          </InputLabel>
        }
        <Tooltip
          content={
            meta.error ? (
              <Typography variant="subheading" color="secondary">
                {meta.error}
              </Typography>
            ) : ''
          }
          open={errorVisible}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          color="white"
          placement="top"
        >
          <ButtonBase
            className={classes.selectRoot}
            onClick={this.onSelectRootClick}
            onFocus={input.onFocus}
            onBlur={this.onSelectRootBlur}
            onKeyDown={this.onSelectRootKeyDown}
            disabled={disabled}
            disableRipple
            id={id}
            aria-label="SelectAutosuggestMenu"
            aria-owns={open ? `${id}-select-autosuggest-menu` : null}
            aria-haspopup="true"
            onMouseOver={this.onButtonMouseOver}
            onMouseOut={this.onButtonMouseOut}
          >
            <div
              className={classNames(classes.selectSelect, {
                [classes.selectSelectSm]: size === 'sm',
                [classes.selectSelectMd]: size === 'md',
                [classes.selectSelectLg]: size === 'lg',
                [classes.selectSelectXl]: size === 'xl',
                [classes.selectSelectLight]: color === 'light',
                [classes.selectSelectDark]: color === 'dark',
                [classes.selectSelectActive]: meta.active,
                [classes.selectSelectDisabled]: disabled,
                [classes.selectSelectError]: meta.touched && meta.error,
              })}
            >
              {input.value.length > 0 ? input.value : placeholder}
            </div>
            <div className={classes.selectAdorment}>
              <IconTriangleBottom className={classes.iconTriangleBottom} />
            </div>
            <input
              {...input}
              type="hidden"
            />
          </ButtonBase>
        </Tooltip>
        <Menu
          id={`${id}-select-autosuggest-menu`}
          anchorEl={this.state.anchorEl}
          open={open}
          onClose={this.onMenuClose}
          list={false}
          arrow={false}
          placement={placement}
          padding="none"
        >
          <AutosuggestField
            input={{
              name: `${input.name}-search`,
              value: this.state.autosuggestValue,
              onChange: this.onAutosuggestChange,
              onFocus: () => {},
              onBlur: () => {},
            }}
            meta={{
              ...meta,
              error: null,
            }}
            id={`${id}-search`}
            limit={limit}
            classes={{
              suggestionsContainer: classes.suggestionsContainerOpen,
              textField: classes.textField,
            }}
            fullWidth
            alwaysRenderSuggestions
            renderSuggestionContent={renderSuggestionContent}
            renderInputContent={renderInputContent}
            onSuggestionSelected={(suggestion) => {
              if (!keepOpenAfterSelection) {
                this.onMenuClose();
              }
              input.onChange(onSuggestionSelected(suggestion));
              return '';
            }}
            fetchSuggestions={fetchSuggestions}
            onListAdornmentClick={onListAdornmentClick}
            listAdornmentText={listAdornmentText}
            onlyConfirmedSelection
            textFieldRef={(textFieldRef) => {
              if (textFieldRef) {
                textFieldRef.focus();
              }
            }}
            keepOpenAfterSelection={keepOpenAfterSelection}
          />
        </Menu>
      </FormControl>
    );
  }
}

SelectAutosuggestField.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  color: PropTypes.oneOf(['light', 'dark']),
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  input: PropTypes.shape({
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
  }).isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    active: PropTypes.bool,
    error: PropTypes.string,
  }),
  renderSuggestionContent: PropTypes.func,
  renderInputContent: PropTypes.func,
  onSuggestionSelected: PropTypes.func,
  fetchSuggestions: PropTypes.func.isRequired,
  onListAdornmentClick: PropTypes.func,
  listAdornmentText: PropTypes.string,
  placeholder: PropTypes.string,
  limit: PropTypes.number,
  placement: PropTypes.oneOf([
    'bottom-left',
    'bottom-middle',
    'bottom-right',
    'top-left',
    'top-middle',
    'top-right',
  ]),
  keepOpenAfterSelection: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    selectRoot: PropTypes.string.isRequired,
    selectSelect: PropTypes.string.isRequired,
    selectSelectSm: PropTypes.string.isRequired,
    selectSelectMd: PropTypes.string.isRequired,
    selectSelectLg: PropTypes.string.isRequired,
    selectSelectXl: PropTypes.string.isRequired,
    selectSelectDark: PropTypes.string.isRequired,
    selectSelectLight: PropTypes.string.isRequired,
    selectSelectActive: PropTypes.string.isRequired,
    selectSelectDisabled: PropTypes.string.isRequired,
    selectSelectError: PropTypes.string.isRequired,
    selectAdorment: PropTypes.string.isRequired,
    iconTriangleBottom: PropTypes.string.isRequired,
    suggestionsContainerOpen: PropTypes.string.isRequired,
    textField: PropTypes.string.isRequired,
  }).isRequired,
};

SelectAutosuggestField.defaultProps = {
  renderSuggestionContent: suggestion => suggestion,
  renderInputContent: suggestion => suggestion,
  onSuggestionSelected: suggestion => suggestion,
  label: '',
  className: '',
  size: 'lg',
  color: 'light',
  disabled: false,
  fullWidth: false,
  placeholder: '',
  onListAdornmentClick: null,
  listAdornmentText: null,
  limit: 10,
  placement: 'bottom-left',
  meta: {
    touched: false,
    error: '',
  },
  keepOpenAfterSelection: false,
};

export default withStyles(styles)(SelectAutosuggestField);



// WEBPACK FOOTER //
// ./src/components/fields/SelectAutosuggestField.js